import { createSlice, type PayloadAction } from '@reduxjs/toolkit'
import charterInitialState, { type CharterState } from '../initialState/charterInitialState'
import type {
  CharterConfig,
  CharterRoundStatus,
  CharterSession,
  MemberPrepSession,
  TeamCharterStatus,
  UnassignedMember,
  RenegotiationRequest,
  CharterVersion,
} from '../types/charterTypes'

const charterSlice = createSlice({
  name: 'charter',
  initialState: charterInitialState as CharterState,
  reducers: {
    setLoading(state, action: PayloadAction<boolean>) {
      state.isLoading = action.payload
    },

    setError(state, action: PayloadAction<string | null>) {
      state.error = action.payload
      state.isLoading = false
    },

    setConfig(state, action: PayloadAction<CharterConfig>) {
      state.config = action.payload
      state.error = null
    },

    setSession(state, action: PayloadAction<CharterSession | null>) {
      state.session = action.payload
    },

    updateSessionStatus(
      state,
      action: PayloadAction<{ status: CharterSession['status']; version?: number }>
    ) {
      if (state.session) {
        state.session.status = action.payload.status
        if (action.payload.version !== undefined) {
          state.session.version = action.payload.version
        }
      }
    },

    setMyProfile(state, action: PayloadAction<MemberPrepSession | null>) {
      state.myProfile = action.payload
    },

    updateMyProfileField(
      state,
      action: PayloadAction<Partial<Pick<MemberPrepSession, 'sharing_mode' | 'visible_sections' | 'profile_approved'>>>
    ) {
      if (state.myProfile) {
        Object.assign(state.myProfile, action.payload)
      }
    },

    setTeamStatuses(state, action: PayloadAction<TeamCharterStatus[]>) {
      state.teamStatuses = action.payload
    },

    setUnassignedMembers(state, action: PayloadAction<UnassignedMember[]>) {
      state.unassignedMembers = action.payload
    },

    setTeamProfiles(state, action: PayloadAction<MemberPrepSession[]>) {
      state.teamProfiles = action.payload
    },

    setRenegotiationRequest(state, action: PayloadAction<RenegotiationRequest | null>) {
      state.renegotiationRequest = action.payload
    },

    setCharterVersions(state, action: PayloadAction<CharterVersion[]>) {
      state.charterVersions = action.payload
    },

    setGateStatus(
      state,
      action: PayloadAction<
        | {
            gate_enabled: boolean
            gate_satisfied: boolean
            teams_total?: number
            teams_ratified?: number
          }
        | null
      >
    ) {
      state.gateStatus = action.payload
    },

    setRoundStatus(state, action: PayloadAction<CharterRoundStatus>) {
      if (state.config) {
        state.config.round_status = action.payload
      }
    },

    setRoundControlLoading(state, action: PayloadAction<string | null>) {
      state.roundControlLoading = action.payload
    },
  },
})

export const {
  setLoading,
  setError,
  setConfig,
  setSession,
  updateSessionStatus,
  setMyProfile,
  updateMyProfileField,
  setTeamStatuses,
  setUnassignedMembers,
  setTeamProfiles,
  setRenegotiationRequest,
  setCharterVersions,
  setGateStatus,
  setRoundStatus,
  setRoundControlLoading,
} = charterSlice.actions

export default charterSlice.reducer
